import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import EastIcon from "@mui/icons-material/East";
import { Chip } from "@mui/material";
import DialogContentText from "@mui/material/DialogContentText"; // Import for confirmation dialog

/**
 * Modal to display full device details and approve/reject billing-type change requests.
 *
 * Props:
 * - open: boolean
 * - device: object with device fields
 * - onClose: () => void
 * - onApprove: (deviceId) => void
 * - onReject: (deviceId) => void
 */
export default function ChangeBillingRequestModal({
  open,
  device,
  onClose,
  onAction,
}) {
  const [confirmDialogOpen, setConfirmDialogOpen] = React.useState(false);
  const [actionType, setActionType] = React.useState(null); // "approve" or "reject"

  const handleActionClick = (type) => {
    setActionType(type);
    setConfirmDialogOpen(true);
  };

  const handleConfirmAction = () => {
    if (actionType === "approve") {
      onAction({
        device_id: device.id,
        action: "approve",
        new_billing_type: device.new_billing_type,
      }); // Call the approve API
    } else if (actionType === "reject") {
      onAction({
        device_id: device.id,
        action: "reject",
        new_billing_type: device.new_billing_type,
      }); // Call the reject API
    }
    setConfirmDialogOpen(false); // Close the confirmation dialog
  };

  const handleCancelAction = () => {
    setConfirmDialogOpen(false); // Close the confirmation dialog
  };

  if (!device) return null;

const getFreePeriodEndDate = (startDate, months) => {
  console.log("Calculating free period end date...", startDate, months);

  const activation = new Date(startDate);

  // Make absolutely sure `months` is a number
  const m = Number(months); 
  if (Number.isNaN(m)) {
    throw new TypeError(`"months" must be numeric, got ${months}`);
  }

  const year  = activation.getUTCFullYear();
  const month = activation.getUTCMonth();
  const day   = activation.getUTCDate();

  // now month + m is numeric addition
  const endDate = new Date(Date.UTC(year, month + m, day));

  const res = endDate.toLocaleDateString("en-GB", {
    day:   "numeric",
    month: "long",
    year:  "numeric",
  });

  console.log("Free period end date:", res);
  return res;
};

  const fields = [
    { label: "Company", value: device.company_name },
    { label: "Device Name", value: device.device_group?.device_name },
    { label: "Device Type", value: device.device_group?.device_type },
    { label: "IMEI", value: device.imei_number },
    { label: "Mac Address", value: device.mac_address },
    { label: "ICCID", value: device.iccid },
    { label: "Serial Number", value: device.serial_number || "-" },
    {
      label: "Free Period",
      value: `${device.free_period.free_time_period} Month`,
    },
    {
      label: "Activated On",
      value: new Date(device.activation_date).toLocaleDateString("en-GB", {
        day: "numeric", // “1”, “2”, … “31”
        month: "long", // “January”, “February”, … “December”
        year: "numeric", // “2024”
      }),
    },
    {
      label: "Requested On",
      value: new Date(
        device.billing_type_change_requested_on
      ).toLocaleDateString("en-GB", {
        day: "numeric", // “1”, “2”, … “31”
        month: "long", // “January”, “February”, … “December”
        year: "numeric", // “2024”
      }),
    },
    // Only include this field if free_time_period is non-zero
    ...(device.free_period.free_time_period > 0
      ? [
          {
            label: "Free Period End On",
            value: getFreePeriodEndDate(
              device.activation_date,
              device.free_period.free_time_period
            ),
          },
        ]
      : []),
    {
      label: "Current Status",
      value: device.status ? "Changed" : "In-Process",
    },
    { label: "Comments", value: device.comments || "-" },
  ];

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }}>
          Billing Change Request Details
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Chip
              label={
                <React.Fragment>
                  {device.billing_type}{" "}
                  <EastIcon sx={{ verticalAlign: "middle" }} />{" "}
                  {device.new_billing_type}
                </React.Fragment>
              }
              sx={{
                backgroundColor: (theme) => theme.palette.primary.dark,
                borderRadius: "16px",
                px: 1.5,
                py: 0.5,
                fontWeight: 500,
                fontSize: "0.875rem",
                marginRight: 1,
                color: (theme) => theme.palette.common.white,
              }}
            />
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2}>
            {fields.map((f) => (
              <React.Fragment key={f.label}>
                <Grid item xs={4}>
                  <Typography variant="subtitle2" gutterBottom>
                    {f.label}
                  </Typography>
                </Grid>
                <Grid item xs={8}>
                  <Typography variant="body2" gutterBottom>
                    {f.value}
                  </Typography>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button
            variant="outlined"
            color="error"
            onClick={() => handleActionClick("reject")}
          >
            Reject
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleActionClick("approve")}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelAction}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Confirm {actionType === "approve" ? "Approval" : "Rejection"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to{" "}
            {actionType === "approve" ? "approve" : "reject"} this request?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAction} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            color={actionType === "approve" ? "primary" : "error"}
            variant="contained"
          >
            {actionType === "approve" ? "Approve" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
